import { Truck } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Field,
  Select,
  type Column,
} from '@/components/ui';
import type { DispensingRx } from '@/features/admin-v2/api';
import { useDeliveries, useDeliverySettings } from '@/features/deliveries/api';
import type {
  DeliveryQueueFilter,
  DeskStatus,
  StaffDelivery,
} from '@/features/deliveries/types';
import queue from '@/pages/queue/queue.module.css';
import styles from './dispensary.module.css';
import { DeliveryStepDialog } from './DeliveryStepDialog';
import {
  NEXT_STEPS,
  REFUSAL_LABEL,
  deliveryStatusLabel,
  deliveryTone,
  isLate,
  klClock,
  klDayClock,
  refusedItems,
  ringgit,
  sameDayState,
} from './delivery-format';

const FILTERS: { value: DeliveryQueueFilter; label: string }[] = [
  { value: 'open', label: 'Open (to pack, out, failed)' },
  { value: 'requested', label: 'Requested' },
  { value: 'packed', label: 'Packed' },
  { value: 'out_for_delivery', label: 'With the courier' },
  { value: 'failed', label: 'Delivery failed' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

const STEP_LABEL: Record<DeskStatus, string> = {
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  failed: 'Failed',
  returned: 'Returned',
  cancelled: 'Cancel',
};

/**
 * RL-1186: the dispensary's same-day delivery queue (GET
 * /dispensary/deliveries) with packing and the courier steps.
 */
export function DeliveriesTab({
  branch,
  prescriptions,
  canManage,
  onPack,
}: {
  branch: { id: string; name: string };
  /** The to-dispense queue, to pack a request and show counter-only items. */
  prescriptions: DispensingRx[] | undefined;
  canManage: boolean;
  onPack: (d: StaffDelivery) => void;
}) {
  const [filter, setFilter] = useState<DeliveryQueueFilter>('open');
  const list = useDeliveries(filter);
  const settings = useDeliverySettings();
  const [now] = useState(() => new Date());
  const [step, setStep] = useState<{ id: string; step: DeskStatus } | null>(
    null,
  );
  const closeStep = useCallback(() => setStep(null), []);
  const rxFor = (d: StaffDelivery) =>
    prescriptions?.find((rx) => rx.prescriptionId === d.prescriptionId);
  const day = sameDayState(settings.data, branch.id, now);
  const live = step ? list.data?.find((d) => d.id === step.id) : undefined;

  const columns: Column<StaffDelivery>[] = [
    {
      key: 'patient',
      header: 'Patient',
      render: (d) => (
        <>
          <p className={queue.name}>{d.patient.fullName}</p>
          <p className={queue.sub}>{d.patient.phone ?? 'No phone'}</p>
          <p className={`${queue.sub} mono`}>{d.prescriptionNumber ?? '-'}</p>
        </>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      render: (d) => <p className={styles.address}>{d.address}</p>,
    },
    {
      key: 'deliverBy',
      header: 'Deliver by',
      render: (d) => (
        <>
          <p>{klDayClock(d.deliverBy, now)}</p>
          <div className={queue.badges}>
            {isLate(d, now) && <Badge tone="danger">Past deliver-by</Badge>}
            <Badge>{d.source === 'app' ? 'App request' : 'Clinic'}</Badge>
          </div>
        </>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (d) => {
        const refused = new Map(
          refusedItems(d.status === 'requested' ? rxFor(d) : undefined).map(
            (r) => [r.medicine, r.reason],
          ),
        );
        return (
          <ul className={styles.items} aria-label="Medicines">
            {d.medicines.map((m) => {
              const reason = refused.get(m);
              return (
                <li key={m}>
                  {m}
                  {reason && (
                    <>
                      {' '}
                      <Badge tone="danger">Not deliverable</Badge>
                      <span className={queue.sub}>
                        {' '}
                        {REFUSAL_LABEL[reason]}
                      </span>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        );
      },
    },
    {
      key: 'fee',
      header: 'Fee',
      render: (d) => (
        <>
          <p>{d.fee.feeSen === null ? 'No fee' : ringgit(d.fee.feeSen)}</p>
          <p className={queue.sub}>
            {d.fee.billId ? 'Fee bill · pay at the counter' : 'No fee bill'}
          </p>
          {d.refundDue && (
            <>
              <Badge tone="danger">Refund due</Badge>
              <p className={queue.sub}>
                Paid, not delivered: refund in Billing
              </p>
            </>
          )}
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => <DeliveryStatusCell d={d} />,
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (d) => {
        if (!canManage) return null;
        const steps = NEXT_STEPS[d.status] ?? [];
        return (
          <div className={`${queue.rowActions} ${queue.noWrap}`}>
            {d.status === 'requested' && (
              <Button size="sm" variant="primary" onClick={() => onPack(d)}>
                Pack for delivery
              </Button>
            )}
            {steps.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={
                  s === 'failed' || s === 'cancelled'
                    ? 'danger'
                    : s === 'returned'
                      ? 'secondary'
                      : 'primary'
                }
                onClick={() => setStep({ id: d.id, step: s })}
              >
                {d.status === 'failed' && s === 'out_for_delivery'
                  ? 'Try again'
                  : STEP_LABEL[s]}
              </Button>
            ))}
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.tab}>
      <div className={styles.toolbar}>
        <Field label="Show">
          <Select
            value={filter}
            options={FILTERS}
            onChange={(event) =>
              setFilter(event.target.value as DeliveryQueueFilter)
            }
          />
        </Field>
        {day && day.offered && (
          <p className={queue.sub}>
            Same-day: ask by {day.cutoff}, delivered by {day.deliverBy}.
          </p>
        )}
      </div>
      {day && !day.offered && (
        <p className={styles.warn} role="status">
          Same-day delivery is not offered from {branch.name} now: patients
          cannot ask for it in the app. Open deliveries below still need their
          courier steps.
        </p>
      )}
      {day?.offered && day.cutoffPassed && (
        <p className={styles.warn} role="status">
          {day.deliverByPassed
            ? `The ${day.deliverBy} deliver-by time has passed: call the patients still waiting, then record failed or returned.`
            : `Cut-off ${day.cutoff} has passed: no new same-day requests today. Deliver the open ones by ${day.deliverBy}.`}
        </p>
      )}
      {!canManage && (
        <p className={queue.sub}>
          View only: packing and courier steps need the dispensing permission.
        </p>
      )}
      <DataTable
        caption="Same-day deliveries"
        columns={columns}
        rows={list.data}
        rowKey={(d) => d.id}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        empty={
          <EmptyState
            icon={<Truck size={22} />}
            title={
              filter === 'open' ? 'No open deliveries' : 'No deliveries here'
            }
            body={
              filter === 'open'
                ? 'Same-day delivery requests from the app appear here until they are delivered, returned or cancelled.'
                : 'No delivery has this status at this branch.'
            }
          />
        }
      />
      <DeliveryStepDialog
        delivery={live ?? null}
        step={live ? step!.step : null}
        onClose={closeStep}
      />
    </div>
  );
}

function DeliveryStatusCell({ d }: { d: StaffDelivery }) {
  const courier = d.courier;
  return (
    <div className={styles.statusCell}>
      <Badge tone={deliveryTone(d.status)} dot>
        {deliveryStatusLabel(d.status)}
      </Badge>
      {courier?.name && (
        <p className={queue.sub}>
          Courier: {courier.name}
          {courier.phone && ` · ${courier.phone}`}
        </p>
      )}
      {courier?.trackingUrl && (
        <a
          className={queue.sub}
          href={courier.trackingUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          Tracking link
        </a>
      )}
      {d.proof && (
        <p className={queue.sub}>
          Received by {d.proof.recipientName ?? '-'}
          {d.proof.deliveredAt && ` at ${klClock(d.proof.deliveredAt)}`}
          {d.proof.idChecked ? ' · IC checked' : ' · IC not checked'}
          {d.proof.note && ` · ${d.proof.note}`}
        </p>
      )}
      {d.status === 'failed' && d.failureReason && (
        <p className={queue.sub}>Failed: {d.failureReason}</p>
      )}
      {d.status === 'cancelled' && (
        <p className={queue.sub}>
          Cancelled by{' '}
          {d.cancelledBy === 'patient' ? 'the patient' : 'the clinic'}
          {d.cancelReason && `: ${d.cancelReason}`}
        </p>
      )}
    </div>
  );
}
